import React, {useMemo} from 'react';
import {view} from '@sail/ui';
import {LinkButton, Text, ChipButton} from 'src/v2/components/common';
import {BrowserExtensionSelect} from 'src/v2/components/walletSelect/BrowserExtensionSelect';
import {WalletSearchGrid} from 'src/v2/components/walletSelect/WalletSearchGrid';
import {WalletSelectGrid} from 'src/v2/components/walletSelect/WalletSelectGrid';
import {getStore, setValue} from 'src/v2/state/store';
import {getWalletIcon} from 'src/v2/util/getWalletIcon';
import {WalletRegistry} from 'src/v2/util/WalletRegistry';
import {filterAvailableWalletOptions} from 'src/v2/util/filterAvailableWalletOptions';
import {isWalletProviderDisabled} from 'src/v2/util/isWalletProviderDisabled';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';
import {wallet} from '@sail/icons/react/Icon';
import {logger} from 'src/utils/logger';
import type {PaymentOption} from 'src/v2/state/types';

const messages = defineMessages({
  connectAnotherWallet: {
    id: 'crypto.payins.v2.wallet_select.connect_another_wallet',
    description: 'Button text to connect a different wallet',
    defaultMessage: 'Connect another wallet',
  },
  allWalletsConnected: {
    id: 'crypto.payins.v2.wallet_select.all_wallets_connected',
    description:
      'Message when all available wallet types are already connected',
    defaultMessage: 'All supported wallet types are already connected.',
  },
  back: {
    id: 'crypto.payins.v2.wallet_select.back',
    description: 'Button text to go back to the connected wallets screen',
    defaultMessage: 'Use connected wallets',
  },
});

export const WalletSelectScreen = ({
  initialShowGrid,
}: {
  initialShowGrid: boolean;
}) => {
  const intl = useIntl();
  const [searchMode, setSearchMode] = React.useState(false);
  const [showGrid, setShowGrid] = React.useState(initialShowGrid);

  const store = getStore();
  const connectedWallets = store.useSelector(
    (state) => state.walletConnection.connectedWallets,
  );
  const supportedCurrencies = store.useSelector(
    (state) => state.supportedCurrencies,
  );
  const disabledWalletProviders = store.useSelector(
    (state) => state.disabledWalletProviders,
  );
  const payFromExchangeEnabled = store.useSelector(
    (state) => state.featureFlags.enable_crypto_payins_pay_from_exchange,
  );

  const allWalletOptions = useMemo(
    () =>
      WalletRegistry.getAllWalletOptions().filter(
        (walletOption) =>
          !isWalletProviderDisabled(walletOption, disabledWalletProviders),
      ),
    [disabledWalletProviders],
  );
  const allExchangeOptions = useMemo(
    () => WalletRegistry.getAllExchangeOptions(),
    [],
  );

  const filteredWalletOptions = filterAvailableWalletOptions(
    allWalletOptions,
    connectedWallets,
    supportedCurrencies,
  );

  const allPaymentOptions: PaymentOption[] = payFromExchangeEnabled
    ? (filteredWalletOptions as PaymentOption[]).concat(allExchangeOptions)
    : filteredWalletOptions;

  const installedExtensionWallets = useMemo(
    () =>
      WalletRegistry.getInstalledExtensionWallets()
        .filter(
          (walletOption) =>
            !isWalletProviderDisabled(walletOption, disabledWalletProviders) &&
            filteredWalletOptions.some(
              (filtered) => filtered.provider === walletOption.provider,
            ),
        )
        .sort((a, b) => {
          // Sort Coinbase Wallet first
          if (a.name === 'Coinbase Wallet') return -1;
          if (b.name === 'Coinbase Wallet') return 1;
          return 0;
        }),
    [disabledWalletProviders, filteredWalletOptions],
  );

  const onBackPress = () => {
    setValue((state) => {
      state.screen = 'WALLET_SWITCH';
    });
  };

  // Handle case where all supported wallet types are already connected
  if (filteredWalletOptions.length === 0) {
    return (
      <view.div css={{stack: 'y', alignX: 'center', gapY: 'space.150'}}>
        <Text type="body">
          <FormattedMessage {...messages.allWalletsConnected} />
        </Text>
        <ChipButton
          icon={wallet}
          onClick={onBackPress}
          label={intl.formatMessage(messages.back)}
        />
      </view.div>
    );
  }

  if (searchMode) {
    return (
      <WalletSearchGrid
        walletOptions={allPaymentOptions}
        onClose={() => setSearchMode(false)}
      />
    );
  }

  if (installedExtensionWallets.length === 0 || showGrid) {
    return (
      <WalletSelectGrid
        walletOptions={allPaymentOptions}
        onSearchPress={() => setSearchMode(true)}
      />
    );
  }

  return (
    <view.div css={{stack: 'y', gapY: 'space.150'}}>
      {installedExtensionWallets.map((walletOption) => (
        <BrowserExtensionSelect
          key={walletOption.provider}
          title={walletOption.name}
          icon={getWalletIcon(walletOption.provider)}
          onClick={() => {
            logger.info('selecting wallet', {
              provider: walletOption.provider,
              view: 'installed_browser_extension_select',
            });
            setValue((state) => {
              state.newWallet = {
                walletOption,
                networkType: null,
              };
              state.screen = 'NETWORK_SELECT';
            });
          }}
        />
      ))}
      <LinkButton
        css={{alignSelf: 'center'}}
        onPress={() => {
          setShowGrid(true);
        }}
      >
        <FormattedMessage {...messages.connectAnotherWallet} />
      </LinkButton>
    </view.div>
  );
};
